import { signOut } from 'firebase/auth';
import { auth } from '~/firebase/init';
import { Flex, Box, FlexProps, useDisclosure, BoxProps } from '@chakra-ui/react';

import Content from './Content';
import Header from '~/components/Header';
import Footer from '~/components/Footer';
import NavDrawer from '~/components/NavDrawer';
import NavBar from '~/components/NavBar';
import LoginModal from '~/components/LoginModal';
import LocationSelectorModal from '~/components/LocationSelectorModal';
import PickupSettingModal from '~/components/PickupSettingModal';

import modalControlAtom from '~/recoil/atom/modalControl';
import authAtom from '~/recoil/atom/auth';
import { useRecoilValue, useRecoilState } from 'recoil';
import { useParams } from 'react-router-dom';

type PageContainerProps = {
  children?: React.ReactNode;
  containerStyles?: FlexProps;
  topBoxStyle?: BoxProps;
  hideHeader?: boolean;
  hideNavBar?: boolean;
  hideFooter?: boolean;
  afterHeaderComponent?: React.ReactNode;
};

export default function PageContainer({
  children,
  containerStyles = {},
  topBoxStyle = {},
  hideFooter = false,
  hideHeader = false,
  hideNavBar = false,
  afterHeaderComponent = null,
}: PageContainerProps) {
  const [
    { isLoginModalOpen, isPickMealPointModalOpen, isPickupSettingModalOPen },
    setModalControl,
  ] = useRecoilState(modalControlAtom);
  const authUser = useRecoilValue(authAtom);
  const { enterpriseGroupBuyId = '' } = useParams();

  const { isOpen, onClose, onOpen } = useDisclosure();

  const handleLoginLogout = () => {
    if (authUser) {
      signOut(auth);
      return;
    }

    setModalControl((prev) => ({ ...prev, isLoginModalOpen: true }));
    onClose();
  };

  const handleLoginModalClose = () => {
    setModalControl((prev) => ({ ...prev, isLoginModalOpen: false }));
  };

  const handlePickMealPointModalClose = () => {
    setModalControl((prev) => ({ ...prev, isPickMealPointModalOpen: false }));
  };

  const handlePickupSettingModalClose = () => {
    setModalControl((prev) => ({ ...prev, isPickupSettingModalOPen: false }));
  };

  return (
    <Flex direction="column" align="stretch" bg="#F7F7F7" minH="100vh" {...containerStyles}>
      <Box pos="sticky" top={0} zIndex={100} {...topBoxStyle}>
        {!hideHeader && (
          <Box as="header" bg="white" px={4} borderBottomColor="primary" borderBottomWidth="1px">
            <Content m="auto">
              <Header onOpen={onOpen} />
            </Content>
          </Box>
        )}

        {afterHeaderComponent}

        <NavBar
          isOpen={isOpen}
          isLogin={!!authUser}
          onClose={onClose}
          onLoginLogout={handleLoginLogout}
          display={{ base: 'none', md: !hideNavBar ? 'block' : 'none' }}
        />
      </Box>

      <Box as="main" flex="1" bg="white">
        {children}
      </Box>

      {!hideFooter && (
        <Box px={4} bgColor="gray.100">
          <Content as="footer" m="auto" py={4}>
            <Footer />
          </Content>
        </Box>
      )}
      <NavDrawer
        isOpen={isOpen}
        isLogin={!!authUser}
        onClose={onClose}
        onLoginLogout={handleLoginLogout}
      />

      <>
        {!enterpriseGroupBuyId ? (
          <LoginModal isOpen={isLoginModalOpen} onClose={handleLoginModalClose} />
        ) : null}

        <LocationSelectorModal
          isOpen={isPickMealPointModalOpen}
          onClose={handlePickMealPointModalClose}
        />
        <PickupSettingModal
          isOpen={isPickupSettingModalOPen}
          onClose={handlePickupSettingModalClose}
        />
      </>
    </Flex>
  );
}
